import React, { useEffect } from "react";
import { FiX } from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import styles from "./QrModal.module.scss";

export type ConsentStatus = "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";

export type QrModalAppointment = {
  id: string;
  patient: string;
  clinician: string;
  time: string;
  appointmentDate?: string;
  email?: string;
  phone?: string;
  consentStatus: ConsentStatus;
};

type Props = {
  open: boolean;
  appt: QrModalAppointment | null;
  onClose: () => void;
};

const statusLabel = (s?: ConsentStatus) =>
  s === "EMAIL_BOUNCED" ? "EMAIL BOUNCED" : s === "NOT_SIGNED" ? "NOT SIGNED" : "SIGNED";

export default function QrModal({ open, appt, onClose }: Props) {
  // close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !appt) return null;

  const { patient, consentStatus, id } = appt;
  const qrLink = `https://dev.patient.medicube.health/${id}`;   

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <span className={styles.headerIcon}>▣</span>
            <span>Scan QR code</span>
          </div>
          <button className={styles.iconBtn} onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Patient details */}
        <div className={styles.details}>
          <div className={styles.col}>
            <div className={styles.field}>
              <div className={styles.label}>Email</div>
              <div className={styles.value}>
                {appt.email ?? `${patient.toLowerCase().replace(/\s+/g, ".")}@example.com`}
              </div>
            </div>
            <div className={styles.field}>
              <div className={styles.label}>Appointment</div>
              <div className={styles.value}>{appt.appointmentDate ?? `Today at ${appt.time}`}</div>
            </div>
          </div>
          <div className={styles.col}>
            <div className={styles.field}>
              <div className={styles.label}>Phone</div>
              <div className={styles.value}>{appt.phone ?? "+44"}</div>
            </div>
            <div className={styles.field}>
              <div className={styles.label}>Status</div>
              <span
                className={`${styles.badge} ${
                  consentStatus === "EMAIL_BOUNCED"
                    ? styles.badgeBounced
                    : consentStatus === "NOT_SIGNED"
                    ? styles.badgeNotSigned
                    : styles.badgeSigned
                }`}
              >
                {statusLabel(consentStatus)}
              </span>
            </div>
          </div>
        </div>

        <hr className={styles.rule} />

        {/* QR Section */}
        <div className={styles.sectionTitle}>Scan QR Code</div>
        <div className={styles.sectionSub}>
          Please scan this QR code with your phone to open and sign your consent form.
        </div>

        <div className={styles.qrRow}>
          <QRCodeSVG
    className={styles.qrImage}
    value={qrLink}  
    size={180}
    bgColor="#ffffff"
    fgColor="#000000"
    includeMargin={true}
  />
          <div className={styles.qrInfo}>
            <div className={styles.qrInfoTitle}>
              This QR code allows patients to quickly access:
            </div>
            <ul className={styles.qrList}>
              <li>Patient consent form</li>
              <li>Appointment details</li>
              <li>Practice information</li>
              <li>Treatment plan</li>
            </ul>
            <div style={{ marginTop: 8, fontSize: "0.9em", wordBreak: "break-all" }}>
              {qrLink}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
